import { createFileRoute, useParams } from '@tanstack/react-router';
import { Suspense, useState } from 'react';

import { FillScreen, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { defaultNS, useTranslation } from '@/lib/i18n';

import AccessibleCategoriesTabContent from './-component/AccessibleCategoriesTabContent';
import DetailTabContent from './-component/DetailTabContent';
import HeaderDetailCategoryPermission from './-component/HeaderDetailCategoryPermission';
import useGetCategoryPermissionDetail from './-query/useGetCategoryPermissionDetail';
import { HEADER_TAB_VALUES, type HeaderTabValue } from './-types';

export const Route = createFileRoute(
  '/_staff/settings/permission-and-roles/_detail/category-permissions/$id/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <Suspense fallback={<ViewportLoader />}>
      <DetailContent />
    </Suspense>
  );
}

function DetailContent() {
  const { id } = useParams({
    from: '/_staff/settings/permission-and-roles/_detail/category-permissions/$id/',
  });

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.settings.permissionAndRoles.categoryPermissionsDetail',
  });

  const { data } = useGetCategoryPermissionDetail({
    categoryLevelAccessPermissionId: id,
  });

  const [activeTab, setActiveTab] = useState<HeaderTabValue>('detail');

  const tabsList = (
    <TabsList className="gap-0">
      {HEADER_TAB_VALUES.map((tab) => (
        <TabsTrigger
          key={tab}
          value={tab}
          className="rounded-none border-b-2 border-transparent px-4 py-2 data-[state=active]:border-primary-fill data-[state=active]:shadow-none"
        >
          {t(
            `tabs.${tab === 'accessible-categories' ? 'accessibleCategories' : tab}`,
          )}
        </TabsTrigger>
      ))}
    </TabsList>
  );

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      validValues={HEADER_TAB_VALUES}
      className="flex flex-col flex-1 min-h-0 overflow-hidden"
    >
      <VStack>
        <HeaderDetailCategoryPermission data={data} bottomContent={tabsList} />

        <FillScreen className="flex flex-col gap-7 px-12 py-5">
          <TabsContent value="detail" className="mt-0">
            <DetailTabContent data={data} />
          </TabsContent>

          <TabsContent
            value="accessible-categories"
            className="flex flex-col flex-1 min-h-0 gap-4 mt-0 pb-6"
          >
            <AccessibleCategoriesTabContent settings={data.settings} />
          </TabsContent>
        </FillScreen>
      </VStack>
    </Tabs>
  );
}
